"use client";

import { createContext, useContext, useRef, useCallback, ReactNode } from "react";

interface AudioPlayerContextType {
  registerPlayer: (id: string, element: HTMLAudioElement) => void;
  unregisterPlayer: (id: string) => void;
  play: (id: string) => void;
  clearCurrentPlayer: (id: string) => void;
}

const AudioPlayerContext = createContext<AudioPlayerContextType | null>(null);

/**
 * Provider for single audio player coordination
 * Ensures only one audio element plays at a time across all PostPlayer components
 */
export function AudioPlayerProvider({ children }: { children: ReactNode }) {
  // Map of player ID to audio element
  const playersRef = useRef<Map<string, HTMLAudioElement>>(new Map());
  // Currently playing player ID
  const currentPlayerIdRef = useRef<string | null>(null);

  const registerPlayer = useCallback((id: string, element: HTMLAudioElement) => {
    playersRef.current.set(id, element);
  }, []);

  const unregisterPlayer = useCallback((id: string) => {
    playersRef.current.delete(id);
    // If this was the current player, clear the reference
    if (currentPlayerIdRef.current === id) {
      currentPlayerIdRef.current = null;
    }
  }, []);

  const clearCurrentPlayer = useCallback((id: string) => {
    // Clear current player if this is the one that was playing
    if (currentPlayerIdRef.current === id) {
      currentPlayerIdRef.current = null;
    }
  }, []);

  const play = useCallback((id: string) => {
    const targetElement = playersRef.current.get(id);
    if (!targetElement) return;

    // Pause ALL other playing audio elements (not just the one we think is playing)
    // This ensures no audio can play simultaneously, even if tracking gets out of sync
    playersRef.current.forEach((element, playerId) => {
      if (playerId !== id) {
        // Always pause other elements, even if we think they're already paused
        // This handles edge cases where state might be inconsistent
        if (!element.paused) {
          element.pause();
        }
      }
    });

    // Also query the DOM directly for any audio elements that might not be registered
    // This is a safety net in case registration fails or is delayed
    if (typeof document !== 'undefined') {
      const allAudioElements = document.querySelectorAll('audio');
      allAudioElements.forEach((audioEl) => {
        // Only pause if it's not our target element and it's playing
        if (audioEl !== targetElement && !audioEl.paused) {
          audioEl.pause();
        }
      });
    }

    // Update current player ID before playing
    currentPlayerIdRef.current = id;

    // Play the target audio
    targetElement.play().catch((error) => {
      console.error("Failed to play audio:", error);
      // If play fails, clear the current player ID
      if (currentPlayerIdRef.current === id) {
        currentPlayerIdRef.current = null;
      }
    });
  }, []);

  return (
    <AudioPlayerContext.Provider
      value={{
        registerPlayer,
        unregisterPlayer,
        play,
        clearCurrentPlayer,
      }}
    >
      {children}
    </AudioPlayerContext.Provider>
  );
}

/**
 * Hook to access audio player context
 * Provides functions to register/unregister players and coordinate playback
 * Returns null if not within AudioPlayerProvider (allows PostPlayer to work without provider)
 */
export function useAudioPlayer(): AudioPlayerContextType | null {
  return useContext(AudioPlayerContext);
}
